import { useNavigate } from 'react-router-dom'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { ResultConceptItemTableRowFragment } from 'generated/resources/graphql'
import { orderBy } from 'lodash'

import Sidepanel from 'modules/core/components/Sidepanel'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { useVisitFWRoutes } from 'modules/finance/components/FinancialWorkspace/hooks/useVisitFW'
import SummaryAmountList from 'modules/payroll_bundle/components/SummaryAmountList'
import type { PayrollResultConceptTotal } from 'modules/payroll_bundle/hooks/useResultConceptTotals'
import {
  getConceptByConceptId,
  getConceptTotalLabel,
} from 'modules/payroll_bundle/hooks/useResultConceptTotals'

interface Props {
  open: boolean
  onClose: () => void
  currentPeriodResultConceptTotals: PayrollResultConceptTotal[]
  otherPeriodResultConceptTotalsByConceptName: { [conceptName: string]: PayrollResultConceptTotal }
  mappedResultConcepts: ResultConceptItemTableRowFragment['concept'][]
  currency: string
}

interface TipProps {
  onClose: () => void
}

const UpsellFinanceWorkspaceTip = ({ onClose }: TipProps) => {
  const navigate = useNavigate()
  const { financialWorkspaceRoute } = useVisitFWRoutes({ source: 'payroll-results-sidepanel' })

  return (
    <Box background="viridian100" borderRadius={{ all: 'abs004' }}>
      <Box
        cursor="pointer"
        onClick={onClose}
        aria-label={i18n.t(
          'payroll_bundle.results_payments.results.side_panel.finance_workspace_tip.close_tip'
        )}
        alignSelf="flexEnd"
        padding="s6"
      >
        <Icon.Small color="grey800" icon="close" />
      </Box>
      <Box paddingX="s24" paddingBottom="s24" gap="s8">
        <Icon.Medium icon="lightbulb" color="viridian800" />
        <Text.Normal size="300" weight="medium" color="grey1000">
          {i18n.t('payroll_bundle.results_payments.results.side_panel.finance_workspace_tip.title')}
        </Text.Normal>
        <Box>
          <Text.Normal>
            {i18n.t(
              'payroll_bundle.results_payments.results.side_panel.finance_workspace_tip.description'
            )}
          </Text.Normal>
        </Box>
        <Box
          paddingTop="s12"
          onClick={() => {
            navigate(financialWorkspaceRoute())
          }}
        >
          <Text.Normal size="200" color="textLink" weight="medium" underlined>
            {i18n.t(
              'payroll_bundle.results_payments.results.side_panel.finance_workspace_tip.link_label'
            )}
          </Text.Normal>
        </Box>
      </Box>
    </Box>
  )
}

const ResultStatsSidePanel = ({
  currentPeriodResultConceptTotals,
  otherPeriodResultConceptTotalsByConceptName,
  mappedResultConcepts,
  currency,
  open,
  onClose,
}: Props) => {
  const sortedResultConceptTotals = orderBy(currentPeriodResultConceptTotals, ['total'], ['desc'])
  const [hideGoToFinanceWorkspaceTip, setHideGoToFinanceWorkspaceTip] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.hideGoToFinanceWorkspaceTip
  )

  const handleTipClose = () => {
    setHideGoToFinanceWorkspaceTip(true)
  }
  const isFinanceTipHidden = !!hideGoToFinanceWorkspaceTip

  const summaryAmounts = sortedResultConceptTotals.map((currentPeriodResultConceptTotal) => {
    const concept = getConceptByConceptId(currentPeriodResultConceptTotal, mappedResultConcepts)
    const conceptName = concept?.name || ''

    return {
      name: getConceptTotalLabel(currentPeriodResultConceptTotal, mappedResultConcepts),
      amountCurrentPeriod: currentPeriodResultConceptTotal.total,
      amountPreviousPeriod: otherPeriodResultConceptTotalsByConceptName[conceptName]?.total,
      currency,
    }
  })

  return (
    <Sidepanel
      overlay={false}
      boxPadding={false}
      width="narrow"
      title={i18n.t('payroll_bundle.results_payments.results.summary.sidepanel_title')}
      onClose={onClose}
      open={open}
      headerBorder
      closeLabel={i18n.t('common.close')}
    >
      <Box padding="s24" width="full" gap="s24" alignItems="center">
        {!isFinanceTipHidden && <UpsellFinanceWorkspaceTip onClose={handleTipClose} />}
        <SummaryAmountList summaryAmountList={summaryAmounts} />
      </Box>
    </Sidepanel>
  )
}

export default ResultStatsSidePanel
